import '../theme.css';
import { Header, Navigation } from '../partials/header';
import { NotLoggedIn } from '../partials/notLoggedIn';
import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../app';
import { calcHarvestResults, calcRegrowResults } from '../utils/profitCalcLogic';

const IS_PRODUCTION = process.env.NODE_ENV === "production";
const BACKEND_URI = IS_PRODUCTION ? process.env.REACT_APP_API_BASE_URI : "http://localhost:5050";

const fertilizerOptions = [
    {
        id : 0,
        name : "No Fertilizer",
        bestPrice : 0
    }
];

function Dropdown({options, title, onSelect}) {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedTitle, setSelectedTitle] = useState(title);
    const dropdownRef = useRef(null);


    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    const handleOptionClick = (option) => {
        console.log(`Selected ${option.name}`);
        setSelectedTitle(option.name);
        setIsOpen(false);

        if(onSelect) {
            onSelect(option);
        }
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownRef]);

    return (
        <div className="dropdown" ref={dropdownRef}> 
        <button onClick={toggleDropdown} className="dropdown-toggle">
            {selectedTitle}
        </button>
        
        {isOpen && (
            <ul className="dropdown-menu">
                {(options || []).map((option) => (
                    <li key={option.id} onClick={() => handleOptionClick(option)}>
                        {option.name}
                    </li>
                ))}
            </ul>
        )}
    </div>
    );
}

function QualityPercentDisplay({cropImg, percentages}) {
    // Make decimal values into friendly whole percentages
    let regularPercent = (percentages[0]*100).toFixed(0);
    let silverPercent = (percentages[1]*100).toFixed(0);
    let goldPercent = (percentages[2]*100).toFixed(0);
    let iridiumPercent = (percentages[3]*100).toFixed(0);
     //   🌽85% 🥈10% 🥇5% 🟣0%
    return (
        //Conditionally displays the different spans for each quality tier, only displaying if the % chance is 
        <div className='quality-percent-display-div'>
            {regularPercent > 0 ? <span className='percent-span'><img className="percent-crop-img" src={`/images/crops/${cropImg}.png`} alt={cropImg}/> <p>{regularPercent}%</p></span> : ""}
            {silverPercent > 0 ? <span className='percent-span'><img className="quality-icon" src={`/images/qualityIcons/Silver_Quality.png`} alt="Silver Quality"/> <p>{silverPercent}%</p></span> : ""}
            {goldPercent > 0 ? <span className='percent-span'><img className="quality-icon" src={`/images/qualityIcons/Gold_Quality.png`} alt="Gold Quality"/> <p>{goldPercent}%</p></span> : ""}
            {iridiumPercent > 0 ? <span className='percent-span'><img className="quality-icon" src={`/images/qualityIcons/Iridium_Quality.png`} alt="Iridium Quality"/> <p>{iridiumPercent}%</p></span> : ""}
        </div>
    )
}

function Calculator() {
    const [selectedCrop, setSelectedCrop] = useState({name : "Unselected"});
    const [selectedFertilizer, setSelectedFertilizer] = useState({name : "Unselected"});
    const [cropData, setCropData] = useState([]);
    const [fertilizerData, setFertilizerData] = useState([]);
    const [isloading, setIsLoading] = useState(true);
    const [plantNumber, setPlantNumber] = useState(0);
    const [tillerSelected, setTillerSelected] = useState(false);
    const [agSelected, setAgSelected] = useState(false);
    const [farmingLevel, setFarmingLevel] = useState(14);
    const [harvestResults, setHarvestResults] = useState(null);
    const [regrowResults, setRegrowResults] = useState(null);
    const [error, setError] = useState("");
    const { user, loading } = useUser();

    useEffect(() => {
        document.title = "Profit Calculator";
        const fetchData = async () => {
            try {
                const cropResp = await fetch(`${BACKEND_URI}/crop`);
                const cropData = await cropResp.json();

                const fertilizerResp = await fetch(`${BACKEND_URI}/fertilizer`);
                const fertilizerData = await fertilizerResp.json();

                setCropData(cropData);
                setFertilizerData(fertilizerData);
                setIsLoading(false);
            } catch (error) {
                console.error("Error fetching crops", error);
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);


    const handleCropChange = (crop) => {
        console.log("Parent recieved crop: ", crop);
        setSelectedCrop(crop);
    };

    const handleFertilizerChange = (fertilizer) => {
        console.log("Parent recieved fertilizer: ", fertilizer);
        setSelectedFertilizer(fertilizer);
    };

    const handlePlantNumberChange = (event) => {
        setPlantNumber(event.target.value);
        console.log(event.target.value);
    }

    const toggleTillerSelected = () => {
        setTillerSelected(!tillerSelected);
        console.log(!tillerSelected);
    }
    
    const toggleAgSelected = () => {
        setAgSelected(!agSelected);
        console.log(!agSelected);
    }   

    const handleCalculate = (e) => {
        e.preventDefault();

        setError("");
        
        if ((selectedCrop.name === "Unselected") || (selectedFertilizer.name === "Unselected")) {
            setError("Please select both a Crop and a Fertilizer!");
            return;
        }
        
        setHarvestResults(calcHarvestResults(selectedCrop, plantNumber, selectedFertilizer, tillerSelected, agSelected, farmingLevel));

        if (selectedCrop.regrowTime) {
            setRegrowResults(calcRegrowResults(selectedCrop, plantNumber, tillerSelected, selectedFertilizer, farmingLevel));
        } else {
            setRegrowResults(null);
        }
    }


    const displayCalcResults = (results) => {
        if (results) {
            return (
                <div className="harvestResults">
                    <h4>{results.isRegrowCrop ? "Profits After First Harvest" : "Profits For One Harvest"}</h4>
                    <ul>
                        <li><p>Grow Time: {results.growTime} days</p></li>
                        <li><p>Total Yield: {results.totalYield} </p><img className="crop-img" src={`/images/crops/${results.cropImgLink}.png`} alt={results.cropImgLink}/></li>
                        <li><p>Crop Quality: </p><QualityPercentDisplay cropImg={results.cropImgLink} percentages={results.qualityPercentages}/></li>
                        <li><p>Avg Profit Mult: {results.avgPriceMultiplier}</p></li>
                        <li><img className="coin-img" src="/images/gold.png" alt="Gold Icon"/><p> Avg Profit: {results.totalProfit}g</p></li>
                        <li><img className="coin-img" src="/images/gold.png" alt="Gold Icon"/><p> Profit Per Day: {results.profitPerDay}g</p></li>

                        <li><img className="coin-img" src={`/images/seeds/${results.cropImgLink}_Seeds.png`} alt={`${results.cropImgLink}_Seeds`}/><p> Seed Costs: {results.seedCost}g</p></li>
                        { results.fertilizerImgLink ? <li><img className="coin-img" src={`/images/fertilizers/${results.fertilizerImgLink}.png`} alt={results.fertilizerImgLink}/><p> Fertilizer Costs: {results.fertilizerCost}g</p></li> : ""}
                        <li><img className="coin-img" src="/images/gold.png" alt="Gold Icon"/><p> Total Cost: {results.totalCost}g</p></li>
                        <li><img className="coin-img" src="/images/gold.png" alt="Gold Icon"/><p> Net Profit: {results.netProfit}g</p></li>
                        <li><img className="coin-img" src="/images/gold.png" alt="Gold Icon"/><p> Net Profit Per Day: {results.netProfitPerDay}g</p></li>
                    </ul>
                </div>
            )
        } 
        return;
    }


    const displayRegrowResults = (results) => {
        if (results) {
            return (
                <div className="regrowResults">
                    <h4>Profits Per Regrow Harvest:</h4>
                    <ul>
                        <li><img className="coin-img" src="/images/gold.png" alt="Gold Icon"/><p>Avg Regrow Profit: {results.regrowProfit}g</p></li>
                        <li><img className="coin-img" src="/images/gold.png" alt="Gold Icon"/><p>Regrow Profit Per Day: {results.regrowProfitPerDay}g</p></li>
                        <li><p>Regrow Time: {results.regrowTime} days</p></li>
                        <li><p>Regrow Yield: {results.regrowYield}</p><img className="crop-img" src={`/images/crops/${results.imgLink}.png`} alt={results.imgLink}/></li>
                    </ul>
                </div>
            )
        }
        return;
    }

    if (loading) {
        return <div className="loading-screen">Checking login...</div>;
    }

    if (!user) {
        return (
            <div>
                <NotLoggedIn />
            </div>
        );
    }

    if (isloading) {
        return <div className="loading-screen">Loading crops...</div>;
    }
    return (
        <div className="theme-container">
            <Navigation />

            <div className="main-content">
                <Header />

                {/* Page Content */}
                <div className="page-content">
                    <div className="content-wrapper">
                        <h1 className="page-heading">Calculate Your Profits!</h1>

                        <div className="content-card">
                            <h2 className="card-title">Calculator</h2>
                            
                            <ul className="calculator-inputs">
                                <li>
                                    <Dropdown
                                        options={cropData}
                                        title="Crop"
                                        onSelect={handleCropChange}
                                    />
                                    
                                    <label className="checkbox-label" htmlFor="plantNumberBox">Number:</label>
                                    <input className="calculator-numberbox" type="number" id="plantNumberBox" name="numberToPlant" value={plantNumber} onChange={handlePlantNumberChange} required/>
                                </li>
                                <li>
                                    <Dropdown
                                        options={[...fertilizerOptions, ...fertilizerData]}
                                        title="Fertilizer"
                                        onSelect={handleFertilizerChange}
                                    />
                                    <label className="checkbox-label" htmlFor="tillerCheckbox">Tiller:</label>
                                    <input className="calculator-checkbox" type="checkbox" id="tillerCheckbox" onChange={() => toggleTillerSelected()}/>
                                    <label className="checkbox-label" htmlFor="agCheckbox">Agriculturalist:</label>
                                    <input className="calculator-checkbox"  type="checkbox" id="agCheckbox" onChange={() => toggleAgSelected()}/>
                                </li>
                                <li>
                                    <label className="checkbox-label" htmlFor="farmingLvlSlider">Farming Lvl:</label>
                                    <input className='calculator-slider' id='farmingLvlSlider' type='range' min={0} max={14} value={farmingLevel} onChange={(e) => setFarmingLevel(parseInt(e.target.value)) } step={1}/>
                                    <span className='farming-lvl-value-span'>{farmingLevel}</span>
                                </li>
                                <li>
                                    <button onClick={handleCalculate} disabled={(selectedCrop.name === "Unselected") || (selectedFertilizer.name === "Unselected")} className={((selectedCrop.name === "Unselected") || (selectedFertilizer.name === "Unselected")) ? "btn-disabled" : "btn-active"}>
                                        Calculate
                                    </button>
                                </li>
                            </ul>
                            {error && (
                                <p className="error-message">
                                    {error}
                                </p>
                            )}

                            <div className="results-div">
                                <div className="harvest-results">
                                    {displayCalcResults(harvestResults)}
                                </div>

                                <div className="regrow-results">
                                    {displayRegrowResults(regrowResults)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Calculator;